'use client';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Image from 'next/image';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import api from '@/app/lib/axios';
import { AxiosError } from 'axios';
import { Check, ShoppingCart, Package, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import { DisplayProduct } from '@/types/product';
import { CoinIcon } from "@/app/components/SvgIcons";

interface PurchaseModalProps {
    product: DisplayProduct | null;
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

// Schema for virtual products (digital)
const virtualOrderSchema = z.object({
    email: z.string().email('Valid email is required'),
    notes: z.string().optional(),
});

// Schema for physical products
const physicalOrderSchema = z.object({
    email: z.string().email('Valid email is required'),
    notes: z.string().optional(),
    first_name: z.string().min(1, 'First name is required'),
    last_name: z.string().min(1, 'Last name is required'),
    phone: z.string().min(1, 'Phone is required'),
    country: z.string().min(1, 'Country is required'),
    city: z.string().min(1, 'City is required'),
    address: z.string().min(1, 'Address is required'),
    postcode: z.string().min(1, 'Postcode is required'),
});

type VirtualOrderData = z.infer<typeof virtualOrderSchema>;
type PhysicalOrderData = z.infer<typeof physicalOrderSchema>;
type OrderFormData = VirtualOrderData | PhysicalOrderData;

export default function PurchaseModal({ product, open, onClose, onSuccess }: PurchaseModalProps) {
    // State hooks - must always be called
    const [purchaseSuccess, setPurchaseSuccess] = useState(false);
    const [orderNumber, setOrderNumber] = useState<string>('');

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [open]);

    // Safe product checks with fallbacks
    const isVirtual = product?.virtual ?? true;
    const schema = isVirtual ? virtualOrderSchema : physicalOrderSchema;

    // Form hook - must always be called
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<OrderFormData>({
        resolver: zodResolver(schema),
        defaultValues: isVirtual ? {
            email: '',
            notes: '',
        } : {
            email: '',
            notes: '',
            first_name: '',
            last_name: '',
            phone: '',
            country: '',
            city: '',
            address: '',
            postcode: '',
        },
    });

    // Style constants
    const inputStyle = "w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition placeholder-gray-600";
    const labelStyle = "block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5";
    const errorStyle = "text-red-300 text-xs mt-1";

    // Handlers
    const handleClose = () => {
        reset();
        setPurchaseSuccess(false);
        setOrderNumber('');
        onClose();
    };

    const onSubmit = async (data: OrderFormData) => {
        if (!product) {
            toast.error('Product not found');
            return;
        }

        try {
            const orderData = {
                ...data,
                items: [
                    {
                        product_id: product.id,
                        quantity: 1
                    }
                ]
            };

            const response = await api.post('orders', orderData);

            // Check if response has status field
            if (response.data.status === false) {
                toast.error(response.data.message || 'Purchase failed', {
                    style: {
                        background: '#1f2937',
                        color: '#fff',
                        borderRadius: '12px',
                        marginTop: '70px',
                    },
                    iconTheme: {
                        primary: '#10b981',
                        secondary: '#1f2937',
                    },
                });
                handleClose(); // Close modal on error
                return;
            }

            // Success response
            if (response.data.status === true) {
                toast.success(response.data.message || 'Purchase successful!', {
                    style: {
                        background: '#1f2937',
                        color: '#fff',
                        borderRadius: '12px',
                        marginTop: '70px',
                    },
                    iconTheme: {
                        primary: '#10b981',
                        secondary: '#1f2937',
                    },
                });
                setPurchaseSuccess(true);
                setOrderNumber(response.data.order?.order_number || response.data.order?.id?.toString() || 'N/A');
                onSuccess();

                // Auto close after 10 seconds
                setTimeout(() => {
                    handleClose();
                }, 10000);
            }
        } catch (error) {
            if (error instanceof AxiosError && error.response) {
                const responseData = error.response.data;

                // Handle error response with status field
                if (responseData.status === false) {
                    toast.error(responseData.message || 'Purchase failed', {
                        style: {
                            background: '#1f2937',
                            color: '#fff',
                            borderRadius: '12px',
                            marginTop: '70px',
                        },
                        iconTheme: {
                            primary: '#ef4444',
                            secondary: '#1f2937',
                        },
                    });
                    handleClose(); // Close modal on error
                    return;
                }

                // Handle validation errors
                const errors = responseData.errors;
                if (errors) {
                    Object.keys(errors).forEach((key) => {
                        toast.error(`${key}: ${errors[key].join(', ')}`, {
                            style: {
                                background: '#1f2937',
                                color: '#fff',
                                borderRadius: '12px',
                                marginTop: '70px',
                            },
                            iconTheme: {
                                primary: '#ef4444',
                                secondary: '#1f2937',
                            },
                        });
                    });
                    handleClose(); // Close modal on validation errors
                } else {
                    toast.error(responseData.message || 'An error occurred', {
                        style: {
                            background: '#1f2937',
                            color: '#fff',
                            borderRadius: '12px',
                            marginTop: '70px',
                        },
                        iconTheme: {
                            primary: '#ef4444',
                            secondary: '#1f2937',
                        },
                    });
                    handleClose(); // Close modal on error
                }
            } else {
                toast.error('An unexpected error occurred', {
                    style: {
                        background: '#1f2937',
                        color: '#fff',
                        borderRadius: '12px',
                        marginTop: '70px',
                    },
                    iconTheme: {
                        primary: '#ef4444',
                        secondary: '#1f2937',
                    },
                });
                handleClose(); // Close modal on unexpected error
            }
        }
    };

    // Early return AFTER all hooks
    if (!open || !product) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={handleClose}
        >
            <div
                className="bg-gray-900 shadow-sm rounded-2xl shadow-2xl w-full max-w-2xl my-4 sm:my-8 overflow-hidden max-h-[calc(100dvh-2rem)] sm:max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >

                {purchaseSuccess ? (
                    // Success Screen
                    <>
                        {/* Header */}
                        <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 p-6">
                            <button
                                onClick={handleClose}
                                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                            >
                                <span className="text-2xl font-bold">×</span>
                            </button>
                            <div className="flex items-center gap-3">
                                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                                    <Check className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-white">Purchase Successful!</h2>
                                    <p className="text-green-100 text-sm">Thank you for your purchase</p>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-4">
                            <div className="rounded-xl bg-gray-800/50 p-4">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Order Number</p>
                                <p className="text-green-400 font-mono font-bold text-xl tabular-nums">#{orderNumber}</p>
                            </div>

                            <div className="rounded-xl bg-gray-800/50 p-4">
                                <p className="text-green-300 text-sm leading-relaxed">
                                    {isVirtual
                                        ? 'Your digital product will be sent to your email shortly.'
                                        : 'Your order has been placed and will be shipped soon.'}
                                </p>
                            </div>

                            <button
                                onClick={handleClose}
                                className="w-full py-3 bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 transition-all duration-300 text-white font-semibold"
                            >
                                Close
                            </button>
                        </div>
                    </>
                ) : (
                    // Purchase Form
                    <>
                        {/* Header */}
                        <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 p-6">
                            <button
                                onClick={handleClose}
                                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                            >
                                <span className="text-2xl font-bold">×</span>
                            </button>
                            <h2 className="text-2xl font-bold text-white">Complete Purchase</h2>
                            <p className="text-green-100 text-sm mt-1">
                                Fill in your details to complete the order
                            </p>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            {/* Product Summary */}
                            <div className="rounded-xl bg-gray-800/50 p-4 mb-6">
                                <div className="flex items-center gap-4">
                                    {product.image_url && (
                                        <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-900 flex items-center justify-center flex-shrink-0">
                                            <Image
                                                src={product.image_url}
                                                alt={product.title}
                                                width={64}
                                                height={64}
                                                className="max-w-full max-h-full object-contain"
                                            />
                                        </div>
                                    )}
                                    <div className="flex-grow">
                                        <h3 className="uppercase text-lg font-bold text-white mb-1">{product.title}</h3>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold tracking-wide flex items-center gap-1 ${isVirtual
                                                ? 'bg-green-500/20 text-green-400'
                                                : 'bg-blue-500/20 text-blue-400'
                                                }`}>
                                                {isVirtual ? <><Zap className="w-3 h-3" />DIGITAL</> : <><Package className="w-3 h-3" />PHYSICAL</>}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {product.discount_percent > 0 && (
                                                <span className="text-sm font-mono tabular-nums text-gray-500 line-through">
                                                    {product.originalValueDisplay}
                                                </span>
                                            )}
                                            <span className="text-xl font-mono font-bold tabular-nums text-green-400">
                                                {product.valueDisplay}
                                            </span>
                                            <CoinIcon size={18} />
                                            {product.discount_percent > 0 && (
                                                <span className="px-2 py-0.5 rounded-md bg-red-500/20 text-red-400 text-[11px] font-mono font-bold">
                                                    -{product.discount_percent}%
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {product.description && (
                                    <div className="mt-4 text-sm text-gray-400 leading-relaxed">
                                        {product.description}
                                    </div>
                                )}
                            </div>

                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                                {/* Email - Required for all products */}
                                <div>
                                    <label htmlFor="email" className={labelStyle}>Email Address *</label>
                                    <input
                                        type="email"
                                        id="email"
                                        {...register('email')}
                                        className={inputStyle}
                                        placeholder="your@email.com"
                                    />
                                    {errors.email && <p className={errorStyle}>{errors.email.message}</p>}
                                </div>

                                {/* Notes - Optional for all products */}
                                <div>
                                    <label htmlFor="notes" className={labelStyle}>Message / Notes (Optional)</label>
                                    <textarea
                                        id="notes"
                                        {...register('notes')}
                                        className={`${inputStyle} h-20`}
                                        placeholder="You can provide here your discord/casino site username..."
                                        rows={3}
                                    />
                                </div>

                                {/* Physical Product Fields */}
                                {!isVirtual && (
                                    <>
                                        <div className="rounded-xl bg-gray-800/50 p-4 mt-4">
                                            <h3 className="text-sm font-bold uppercase tracking-wide text-emerald-400 mb-4">Shipping Information</h3>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label htmlFor="first_name" className={labelStyle}>First Name *</label>
                                                    <input
                                                        type="text"
                                                        id="first_name"
                                                        {...register('first_name' as keyof PhysicalOrderData)}
                                                        className={inputStyle}
                                                        placeholder="Your name"
                                                    />

                                                </div>

                                                <div>
                                                    <label htmlFor="last_name" className={labelStyle}>Last Name *</label>
                                                    <input
                                                        type="text"
                                                        id="last_name"
                                                        {...register('last_name' as keyof PhysicalOrderData)}
                                                        className={inputStyle}
                                                        placeholder="Your surname"
                                                    />

                                                </div>
                                            </div>

                                            <div className="mt-4">
                                                <label htmlFor="phone" className={labelStyle}>Phone Number *</label>
                                                <input
                                                    type="tel"
                                                    id="phone"
                                                    {...register('phone' as keyof PhysicalOrderData)}
                                                    className={inputStyle}
                                                    placeholder="+1 234 567 8900"
                                                />

                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                                <div>
                                                    <label htmlFor="country" className={labelStyle}>Country *</label>
                                                    <input
                                                        type="text"
                                                        id="country"
                                                        {...register('country' as keyof PhysicalOrderData)}
                                                        className={inputStyle}
                                                        placeholder="United States"
                                                    />

                                                </div>

                                                <div>
                                                    <label htmlFor="city" className={labelStyle}>City *</label>
                                                    <input
                                                        type="text"
                                                        id="city"
                                                        {...register('city' as keyof PhysicalOrderData)}
                                                        className={inputStyle}
                                                        placeholder="New York"
                                                    />

                                                </div>
                                            </div>

                                            <div className="mt-4">
                                                <label htmlFor="address" className={labelStyle}>Address *</label>
                                                <input
                                                    type="text"
                                                    id="address"
                                                    {...register('address' as keyof PhysicalOrderData)}
                                                    className={inputStyle}
                                                    placeholder="123 Main St, Apt 4B"
                                                />

                                            </div>

                                            <div className="mt-4">
                                                <label htmlFor="postcode" className={labelStyle}>Postcode / ZIP *</label>
                                                <input
                                                    type="text"
                                                    id="postcode"
                                                    {...register('postcode' as keyof PhysicalOrderData)}
                                                    className={inputStyle}
                                                    placeholder="10001"
                                                />

                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Submit Buttons */}
                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        className="flex-1 py-3 rounded-xl bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 transition-colors font-semibold"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="flex-1 py-3 bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 transition-all duration-300 text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="animate-spin h-5 w-5 border-t-2 border-b-2 border-white rounded-full"></span>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <ShoppingCart className="w-5 h-5" />
                                                Complete
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </>
                )}
            </div>
        </div >
    );
}